import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD } from '../utils/constants';

const DEFAULT = {
  company_name: 'Brahmaputra Cruise Private Limited',
  address: '289, A T Road, Maa Padma Commercial Complex, Guwahati, Assam - 781009',
  gstin: '18AACCB7255M1ZA',
  phone: '9435558001',
  email: 'info@bcpl.in',
  cruise_sac_code: '996415',
  cruise_gst_rate: 18,
  food_sac_code: '996331',
  food_gst_rate: 5,
};

const cardStyle = {
  background: '#fff',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  padding: '22px 24px',
  maxWidth: 580,
  boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
  marginBottom: 16,
};

const lbl = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'var(--color-ink-secondary)',
  marginBottom: 4,
};

const inp = (extra = {}) => ({
  width: '100%',
  padding: '9px 12px',
  border: '1.5px solid var(--color-border-strong)',
  borderRadius: 8,
  fontSize: '0.88rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
  color: NAVY,
  ...extra,
});

function Field({ label, name, value, onChange, type = 'text', rows }) {
  const shared = {
    value: value ?? '',
    onChange: (e) => onChange(name, e.target.value),
    style: rows ? { ...inp(), resize: 'vertical', lineHeight: 1.5 } : inp(),
  };
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={lbl}>{label}</label>
      {rows ? <textarea rows={rows} {...shared} /> : <input type={type} {...shared} />}
    </div>
  );
}

export default function InvoiceSettings({ onNavigate }) {
  const [form, setForm]         = useState(DEFAULT);
  const [settingsId, setSettingsId] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    supabase.from('invoice_settings').select('*').limit(1).maybeSingle().then(({ data }) => {
      if (data) {
        setSettingsId(data.id);
        setForm({ ...DEFAULT, ...data });
      }
      setLoading(false);
    });
  }, []);

  const handleChange = (name, val) => {
    setSaved(false);
    setForm(prev => ({ ...prev, [name]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = settingsId ? { id: settingsId, ...form } : form;
    const { error: err } = await supabase
      .from('invoice_settings')
      .upsert(payload);
    setSaving(false);
    if (err) { setError(err.message); return; }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading) {
    return (
      <div>
        <div className="back-row">
          <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
        </div>
        <p style={{ color: 'var(--color-ink-faint)', padding: 20 }}>Loading…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: NAVY }}>Invoice Settings</h2>
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 20 }}>Company Details</h3>
        <Field label="Company Name"  name="company_name" value={form.company_name} onChange={handleChange} />
        <Field label="Address"       name="address"      value={form.address}      onChange={handleChange} rows={3} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="GSTIN"  name="gstin" value={form.gstin} onChange={handleChange} />
          <Field label="Phone"  name="phone" value={form.phone} onChange={handleChange} />
        </div>
        <Field label="Email" name="email" value={form.email} onChange={handleChange} />
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 20 }}>GST Configuration</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Cruise SAC Code"    name="cruise_sac_code"  value={form.cruise_sac_code}  onChange={handleChange} />
          <Field label="Cruise GST Rate (%)" name="cruise_gst_rate" value={form.cruise_gst_rate}  onChange={handleChange} type="number" />
          <Field label="Food SAC Code"      name="food_sac_code"    value={form.food_sac_code}    onChange={handleChange} />
          <Field label="Food GST Rate (%)"  name="food_gst_rate"    value={form.food_gst_rate}    onChange={handleChange} type="number" />
        </div>
      </div>

      {error &&<p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: 12, maxWidth: 580 }}>{error}</p>}

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          background: saved ? '#15803d' : NAVY,
          color: saved ? '#fff' : GOLD,
          border: 'none', borderRadius: 10,
          padding: '11px 28px', fontSize: '0.9rem', fontWeight: 700,
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.8 : 1,
          transition: 'background 0.2s',
        }}
      >
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Invoice Settings'}
      </button>
    </div>
  );
}
